import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearchSelect = useCallback((url: string) => {
    setSearchOpen(false);
    setSearchQuery('');
    window.open(url, '_blank', 'noopener,nofollow');
  }, []);

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70'
          : 'bg-background-50/85 backdrop-blur-sm border-b border-background-200/40'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">

          {/* Left: Logo + Home */}
          <div className="flex items-center gap-4 md:gap-6 shrink-0">
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-950 hover:text-primary-500 transition-colors duration-200 shrink-0"
            >
              <span className="font-heading text-xl md:text-2xl font-bold tracking-tight">
                superbuy<span className="text-primary-500">.baby</span>
              </span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className="px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line mr-1"></i>
                Home
              </Link>
              <Link
                to="/superbuy-spreadsheet-guide"
                className="px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-1"></i>
                Shopping Guide
              </Link>
            </div>
          </div>

          {/* Center: Search + Category Dropdown */}
          <div className="hidden md:flex items-center gap-3 flex-1 max-w-xl mx-6">
            {/* Search Bar */}
            <div ref={searchRef} className="relative flex-1">
              <div className="flex items-center bg-background-100 border border-background-300/60 rounded-full px-4 py-2 transition-all duration-200 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Find spreadsheets, guides, and product resources..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-800 placeholder:text-foreground-300 ml-2 font-body"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                    className="text-foreground-400 hover:text-foreground-600 shrink-0 cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
              {searchOpen && searchQuery && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden z-50 max-h-60 overflow-y-auto">
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleSearchSelect(cat.url)}
                        className="w-full text-left px-4 py-3 hover:bg-background-100 transition-colors flex items-center gap-3 text-sm text-foreground-700 cursor-pointer"
                      >
                        <i className="ri-price-tag-3-line text-primary-400"></i>
                        <span>{cat.name}</span>
                        <i className="ri-arrow-right-up-line text-foreground-300 ml-auto text-xs"></i>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-foreground-400">
                      No results found for &ldquo;{searchQuery}&rdquo;
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300/60 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line text-primary-400"></i>
                <span className="font-medium">Categories</span>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden z-50 w-56 py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: CTA Buttons */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Superbuy Spreadsheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-foreground-800 text-background-50 text-sm font-semibold hover:bg-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer rounded-lg hover:bg-background-100 transition-colors"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200 py-4 pb-6 space-y-4">
            {/* Mobile nav links */}
            <div className="flex items-center gap-2">
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Home
              </Link>
              <Link
                to="/superbuy-spreadsheet-guide"
                onClick={closeMobile}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Shopping Guide
              </Link>
            </div>

            {/* Mobile Search */}
            <div className="relative">
              <div className="flex items-center bg-background-100 border border-background-300/60 rounded-full px-4 py-2.5">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search spreadsheets & guides..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-800 placeholder:text-foreground-300 ml-2"
                />
              </div>
              {searchOpen && searchQuery && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-background-50 border border-background-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                  {filteredCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSearchSelect(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Categories - enhanced with icons */}
            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-1 py-2 text-xs font-semibold uppercase tracking-wider text-foreground-500 cursor-pointer"
              >
                <span>Categories ({categories.length})</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 mt-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobile}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <span className="w-6 h-6 rounded-md bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-price-tag-3-line text-xs text-primary-500"></i>
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile CTAs */}
            <div className="flex flex-col gap-2 pt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all"
              >
                Superbuy Spreadsheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-foreground-800 text-background-50 text-sm font-semibold hover:bg-foreground-950 transition-all"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}